/**
 * Generic helpers for rendering *incomplete* JSON while it is still being
 * streamed (e.g. an agent writing a tool call's JSON input token by token).
 *
 * `completePartialJson` turns a syntactically-truncated JSON string into the
 * longest valid prefix it can, by closing open strings/brackets/braces.
 * `pruneFlatElementSpec` prunes a flat `{ root, elements }` element-tree spec
 * down to what is currently complete, so a renderer can draw it mid-stream.
 *
 * Both are intentionally domain-agnostic: the email-template editor and the
 * in-chat custom-view/chart previews share them.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Balance a partial JSON string by closing open strings, arrays, and objects.
 *
 * Streamed JSON can be truncated mid-token in many ways (an open string, a
 * written key with no value, a dangling comma or colon, a half-typed literal).
 * Rather than enumerate every case, we find the longest *safe prefix* — the
 * prefix ending at a token boundary where the value most recently added to
 * each open container is complete — then append the closing brackets for the
 * still-open containers. The result is parsed to confirm validity, trimming
 * back further if the first attempt fails.
 */
export function completePartialJson(input: string): string | null {
  const text = input.trimEnd();
  if (!text) return null;

  // Boundaries (1-based end indices) after which the text-so-far ends on a
  // completed value within its container: a closed string, a closed bracket,
  // or a finished literal followed by a structural char. We try the longest
  // first and fall back to shorter ones until one balances and parses.
  const boundaries = collectSafeBoundaries(text);

  for (let i = boundaries.length - 1; i >= 0; i--) {
    const candidate = balanceAndClose(text.slice(0, boundaries[i]));
    if (candidate === null) continue;
    try {
      JSON.parse(candidate);
      return candidate;
    } catch {
      // try a shorter prefix
    }
  }
  return null;
}

/**
 * End indices (exclusive) of prefixes that plausibly end on a complete value.
 * A boundary sits immediately after a closing quote, `}`, `]`, or a literal
 * token (number/true/false/null) — i.e. wherever a value could legally end.
 */
function collectSafeBoundaries(text: string): number[] {
  const boundaries: number[] = [];
  let inString = false;
  let escaped = false;
  let literalRun = false;

  function flushLiteral(end: number) {
    if (literalRun) {
      boundaries.push(end);
      literalRun = false;
    }
  }

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') {
        inString = false;
        boundaries.push(i + 1);
      }
      continue;
    }
    switch (ch) {
      case '"':
        flushLiteral(i);
        inString = true;
        break;
      case '}':
      case ']':
        flushLiteral(i);
        boundaries.push(i + 1);
        break;
      case '{':
      case '[':
        // A position right after an opening bracket is a safe fallback: it
        // yields an empty container when the first key/value is incomplete.
        flushLiteral(i);
        boundaries.push(i + 1);
        break;
      case ':':
      case ',':
        flushLiteral(i);
        break;
      case ' ':
      case '\n':
      case '\r':
      case '\t':
        flushLiteral(i);
        break;
      default:
        // part of a literal token (number / true / false / null)
        literalRun = true;
        break;
    }
  }
  flushLiteral(text.length);
  return boundaries;
}

/**
 * Append the closing brackets needed to balance `text`. Returns null if a
 * string is still open (the prefix was not cut at a safe boundary) or if the
 * prefix ends on a dangling `:` / `,` that can't be closed.
 */
function balanceAndClose(prefix: string): string | null {
  let trimmed = prefix.trimEnd();
  // A boundary can legitimately leave a trailing comma (e.g. after a closed
  // value) — strip dangling separators before closing.
  while (trimmed.endsWith(',') || trimmed.endsWith(':')) {
    trimmed = trimmed.slice(0, -1).trimEnd();
  }
  if (!trimmed) return null;

  const stack: Array<'{' | '['> = [];
  let inString = false;
  let escaped = false;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' || ch === ']') stack.pop();
  }
  if (inString) return null;

  const closers = stack
    .reverse()
    .map((open) => (open === '{' ? '}' : ']'))
    .join('');
  return trimmed + closers;
}

/**
 * Prune a flat `{ root, elements }` element-tree spec so every retained
 * element is complete and only references children that exist. Returns null
 * when no root element is renderable yet.
 *
 * Shared by the email-template spec and the internal-UI custom-view spec,
 * which have the identical `{ root, elements: { id: { type, props, children } } }`
 * shape.
 */
export function pruneFlatElementSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> | null {
  const root = spec.root;
  if (typeof root !== 'string' || !root.trim()) return null;
  if (!isRecord(spec.elements)) return null;

  const sourceElements = spec.elements;

  function isCompleteElement(value: unknown): value is Record<string, unknown> {
    return (
      isRecord(value) &&
      typeof value.type === 'string' &&
      value.type.trim().length > 0
    );
  }

  if (!isCompleteElement(sourceElements[root])) return null;

  const pruned: Record<string, unknown> = {};

  for (const [key, element] of Object.entries(sourceElements)) {
    if (!isCompleteElement(element)) continue;
    const next: Record<string, unknown> = { ...element };
    if (Array.isArray(element.children)) {
      next.children = element.children.filter(
        (child) =>
          typeof child === 'string' && isCompleteElement(sourceElements[child]),
      );
    } else if ('children' in element) {
      // Streaming can truncate `children` into a scalar/object — drop the
      // malformed value rather than leaking it into the rendered spec.
      delete next.children;
    }
    pruned[key] = next;
  }

  if (!pruned[root]) return null;

  return { ...spec, root, elements: pruned };
}
